import DocumentViewerModal from "components/DocumentViewer";
import FileRow from "components/file/file-row";
import TeamView from "components/team_view";
import { useState } from "react";
import { LegalTranslation } from "types";
import { formatDay } from "utils/helpers";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import FileUpload from "components/FileUpload";
import DynamicFormInput from "components/form/form";
import { uploadLegalTranslationDocument } from "services/legalTranslationService";
import { LoadingIcon } from "utils/constant";
import { useTranslation } from "react-i18next";

interface singleLegalTranslationaperDetailPops {
  personImg: any;
  research: LegalTranslation;
  fileRowProps?: any;
}

export default function SingleLegalTranslationapersDetail({
  personImg,
  research,
  fileRowProps,
}: singleLegalTranslationaperDetailPops) {
  const { t } = useTranslation();
  const [view, setView] = useState(false);
  const [showPrevious, setShowPrevious] = useState(false);
  const { user } = useAuth();
  const { showToast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [completionFile, setCompletionFile] = useState<File | null>(null);
  const [draftFile, setDraftFile] = useState<File | null>(null);

  const isInternal = user?.roles && user.roles.length > 0;

  // Get team information
  const teamMembers = research?.prosecutor_assignments || [];
  const teamLeader = teamMembers.find((p: any) => p?.is_team_leader);
  const totalMembers = teamMembers.length;

  const isRegistrar = user.roles?.includes("registrar");

  const handleUploadFiles = async () => {
    const hasDraft = draftFile !== null;
    const hasCompletion = completionFile !== null;

    if (!hasDraft && !hasCompletion) {
      showToast(t("PleaseSelectAtLeastOneFile"), "error");
      return;
    }

    if (hasCompletion && !referenceNumber.trim()) {
      showToast(t("PleaseEnterReferenceNumberForCompletion"), "error");
      return;
    }

    setUploading(true);

    try {
      // Upload draft file if selected
      if (hasDraft) {
        const draftData = new FormData();
        draftData.append("document_type", "draft_translation_file");
        draftData.append("translation_request", research?.id || "");
        draftData.append("document_uuid", crypto.randomUUID());
        draftData.append("document_path", draftFile.name);
        draftData.append("document", draftFile);
        await uploadLegalTranslationDocument(draftData);
      }

      // Upload completion letter if selected
      if (hasCompletion) {
        const completionData = new FormData();
        completionData.append("document_type", "traslation_completion_letter_file");
        completionData.append("translation_request", research?.id || "");
        completionData.append("document_uuid", crypto.randomUUID());
        completionData.append("document_path", completionFile.name);
        completionData.append("document", completionFile);
        await uploadLegalTranslationDocument(completionData);
      }

      showToast(t("FilesUploadedSuccessfully"), "success");
      setDraftFile(null);
      setCompletionFile(null);
      setReferenceNumber("");
    } catch (error) {
      console.error("Upload error:", error);
      showToast(t("FailedToUploadFiles"), "error");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div className="flex flex-col bg-white rounded-lg shadow-md px-8 p-6 mb-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="font-semibold text-3xl text-[#212121] mb-1">
              {t("OfficialLegalTranslationPublished")}
            </h1>
          </div>
        </div>

        <div className="flex gap-1 text-[#9D0303] font-medium text-sm mb-1">
          <p>{formatDay(research?.request_last_updated_date || research?.updated_at).weekday},</p>
          <p>{formatDay(research?.request_last_updated_date || research?.updated_at).rest}</p>
        </div>

        {/* Translated Summary with proper paragraph formatting */}
        <div className="mt-4 mb-6">
          {research?.translated_summary ? (
            <div className="space-y-4">
              {research.translated_summary.split("\n").map(
                (paragraph: string, index: number) =>
                  paragraph.trim() !== "" && (
                    <p
                      key={index}
                      className="font-light text-base text-[#212121] leading-relaxed"
                    >
                      {paragraph}
                    </p>
                  )
              )}
            </div>
          ) : (
            <p className="font-light text-base text-gray-500 italic">
              {t("NoTranslationSummaryAvailable")}
            </p>
          )}
        </div>

        {/* Team Section */}
        <div className="flex mt-5 mb-3 rounded-lg p-4 bg-gray-5">
          <div className="flex w-full items-center">
            {/* Team Leader Avatar with badge */}
            <div className="relative mr-4">
              <img
                src={personImg}
                alt={t("TeamLeaderAvatarAlt")}
                width={48}
                height={48}
                className="object-cover rounded-full"
              />
              {totalMembers > 1 && (
                <div className="absolute -bottom-1 -right-1 bg-gray-400 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-medium">
                  +{totalMembers - 1}
                </div>
              )}
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-[#0A1D39] font-semibold text-lg">
                  {teamLeader?.prosecutor_name || t("Unknown")}
                </p>
                {totalMembers > 1 && (
                  <span className="text-gray-500 text-sm font-normal">
                    {t("PlusMoreMembers", { count: totalMembers - 1 })}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-4">
                <p className="text-[#9D0303] font-medium text-sm">
                  {t("LegalTranslator")}
                </p>
                {totalMembers > 0 && (
                  <div className="flex items-center text-gray-600 text-sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-1"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    {t("MemberCount", { count: totalMembers })}
                  </div>
                )}
              </div>
            </div>

            {/* View Team Button */}
            <button
              onClick={() => setView(!view)}
              className="flex items-center gap-2 text-gray-700 hover:text-gray-400 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
              title={t("ViewTeam")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
              >
                <mask
                  id="mask0_9_31087"
                  maskUnits="userSpaceOnUse"
                  x="1"
                  y="5"
                  width="22"
                  height="14"
                >
                  <path
                    d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z"
                    fill="white"
                    stroke="white"
                    strokeWidth="1.33333"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z"
                    fill="black"
                    stroke="black"
                    strokeWidth="1.33333"
                    strokeLinejoin="round"
                  />
                </mask>
                <g mask="url(#mask0_9_31087)">
                  <path d="M0 0H24V24H0V0Z" fill="#EFC83D" />
                </g>
              </svg>
              <span className="font-medium text-sm">{t("ViewTeam")}</span>
            </button>
          </div>
        </div>

        {/* Registrar Upload Section */}
        {isRegistrar && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <h3 className="font-semibold text-lg mb-4">{t("UploadFiles")}</h3>
            <div className="space-y-4">
              <DynamicFormInput
                inputs={[
                  {
                    value: referenceNumber,
                    type: "text",
                    label: t("ReferenceNumberForCompletionLetter"),
                    name: "reference_number",
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setReferenceNumber(e.target.value),
                    placeholder: t("EnterReferenceNumber"),
                  },
                ]}
              />
              <div>
                <h4 className="font-medium mb-2">{t("DraftTranslationFileOptional")}</h4>
                <FileUpload
                  label={t("SelectDraftFile")}
                  onChange={setDraftFile}
                />
              </div>
              <div>
                <h4 className="font-medium mb-2">{t("TranslationCompletionLetterOptional")}</h4>
                <FileUpload
                  label={t("SelectCompletionLetter")}
                  onChange={setCompletionFile}
                />
              </div>
              <button
                onClick={handleUploadFiles}
                disabled={uploading}
                className="bg-[#212121] hover:bg-[#141414] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded-md"
              >
                {uploading ? (
                  <div className="flex items-center justify-center">
                    {LoadingIcon} {t("Uploading")}
                  </div>
                ) : (
                  t("UploadFiles")
                )}
              </button>
            </div>
          </div>
        )}

        {/* Document Viewer Section */}
        <div className="flex justify-between p-3 border border-gray-300 rounded-lg gap-2 mb-2 flex-col">
          {(() => {
            const draftFiles = (research?.documents || []).filter(
              (document: any) =>
                document.document_type === "draft_translation_file"
            ).sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
            
            if (!draftFiles || draftFiles.length === 0) return null;
            const latest = draftFiles[0];
            const previous = draftFiles.slice(1);
            return (
              <>
                <DocumentViewerModal
                  label={t("TranslatedFile")}
                  documents={[latest]}
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_translation/legal-translation-documents/document`}
                />
                {isInternal && previous.length > 0 && (
                  <div className="mt-4">
                    <button
                      onClick={() => setShowPrevious(!showPrevious)}
                      className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
                    >
                      {showPrevious ? t("Hide") : t("Show")} {t("PreviousVersions", { count: previous.length })}
                    </button>
                    {showPrevious && (
                      <div className="mt-2 space-y-2">
                        {previous.map((doc: any, index: number) => (
                          <DocumentViewerModal
                            key={doc.id}
                            label={`${t("PreviousVersion")} ${index + 1}`}
                            documents={[doc]}
                            baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_translation/legal-translation-documents/document`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Team View Modal */}
      {view && (
        <TeamView
          onClose={() => setView(false)}
          teamMembers={research?.prosecutor_assignments}
        />
      )}
    </>
  );
}